/**
 * Workflow Runner — orchestrates a DSL workflow as a sequence of task executions
 *
 * Programmatic control flow that replaces LLM-driven step interpretation:
 *
 *   1. Load workflow contract (ordered steps)
 *   2. Run plugin beforeWorkflow hooks
 *   3. For each step:
 *      - delegate → call task-runner with followUp/retry recovery
 *      - gate    → pause and wait for user input
 *   4. Validate each result against handoff schema
 *   5. Accumulate context between steps
 *   6. Run plugin afterWorkflow hooks
 */

import { pluginRegistry } from "./plugin.js";
import { runTask, type SdkAdapter, type TaskContext, type TaskOutcome, type TaskRunResult, type AgentContractLike, type TaskContractLike, type TokenUsage } from "./task-runner.js";
import type { LibraryToolRegistry } from "./library-tool-binding.js";
import type { ModelClass, ResolvedModel } from "./model-resolver.js";
import type { z } from "zod";
import {
  createExecutionEnvelope,
  emitSessionOpen,
  emitSessionClose,
  emitIterationOpen,
  emitIterationClose,
  emitIterationMetric,
  type ObserverEmitFn,
} from "./observability-bridge.js";
import { computeCostUsd } from "./model-pricing.js";
import { runToolchainChecks, type RunToolchainChecksOptions } from "./toolchain-checker.js";
import {
  WORKFLOW_INPUT_STEP_ID,
  buildWorkflowInputOutput,
  formatWorkflowInputContext,
  stepDependsOnWorkflowInput,
  type WorkflowInputOutput,
} from "./workflow-input.js";

export type { ObserverEmitFn } from "./observability-bridge.js";

// ---------------------------------------------------------------------------
// Adapter factory — enables true parallelism with stateful adapters
// ---------------------------------------------------------------------------

/**
 * A factory that creates independent SdkAdapter instances.
 * Used by runWorkflow to allocate one adapter per parallel step,
 * avoiding session-level serialization in stateful SDK adapters.
 */
export type SdkAdapterFactory = () => SdkAdapter;

/**
 * Model-aware adapter factory. Accepts a model_class and returns an adapter
 * configured for that capability level (possibly a different provider/model).
 */
export type ModelAwareSdkAdapterFactory = (modelClass?: ModelClass) => SdkAdapter | Promise<SdkAdapter>;

/** Accepts either a single adapter (serial/shared) or a factory (parallel-safe). */
export type AdapterOrFactory = SdkAdapter | SdkAdapterFactory | ModelAwareSdkAdapterFactory;

function isAdapterFactory(v: AdapterOrFactory): v is SdkAdapterFactory | ModelAwareSdkAdapterFactory {
  return typeof v === "function" && !("send" in v);
}

function resolveAdapter(adapterOrFactory: AdapterOrFactory): { adapter: SdkAdapter; factory: (SdkAdapterFactory | ModelAwareSdkAdapterFactory) | undefined } {
  if (isAdapterFactory(adapterOrFactory)) {
    const result = adapterOrFactory();
    if (result && typeof (result as Promise<SdkAdapter>).then === "function") {
      throw new Error("ModelAwareSdkAdapterFactory returned a Promise for the default adapter. Use a synchronous factory or pre-resolve the default adapter.");
    }
    return { adapter: result as SdkAdapter, factory: adapterOrFactory };
  }
  return { adapter: adapterOrFactory, factory: undefined };
}

// ---------------------------------------------------------------------------
// Step types (duck-typed — generated contracts provide concrete values)
// ---------------------------------------------------------------------------

/** Specification for what a step receives from a dependency. */
export interface ReceiveSpec {
  /** The task id of the dependency step to receive from. If omitted, alias is used as task id. */
  readonly from?: string;
  /** JSONPath-like dotted path to extract from the step result (e.g. "$.changed_files"). Simplified: just use dot notation like "changed_files" or "checks_performed". If omitted, entire result is received. */
  readonly path?: string;
}

export interface DelegateStep {
  readonly type: "delegate";
  readonly task: string;
  readonly from_agent: string;
  readonly description: string;
  readonly optional: boolean;
  readonly max_retries: number;
  readonly max_follow_ups?: number;
  readonly depends_on?: readonly string[];
  readonly instruction?: string;
  readonly receives?: Record<string, ReceiveSpec>;
  readonly retry?: {
    readonly condition: string;
    readonly fix_task: string;
    readonly revalidate_task?: string;
  };
}

export interface GateStep {
  readonly type: "gate";
  readonly gate_kind: string;
  readonly description: string;
  readonly depends_on?: readonly string[];
}

/**
 * Evaluate step — runs a task that returns a convergence-envelope, then either
 * proceeds (verdict=pass) or loops back to `loop_to` with feedback injected
 * (verdict=fail, iteration < max_iterations), or applies `on_exhausted`.
 */
export interface EvaluateStep {
  readonly type: "evaluate";
  readonly task: string;
  readonly from_agent: string;
  readonly evaluator_agent?: string;
  readonly description?: string;
  readonly loop_to: string;
  readonly max_iterations: number;
  readonly inject_as?: string;
  readonly on_exhausted?: "abort" | "fail_partial" | "escalate";
  readonly depends_on?: readonly string[];
}

/**
 * Payload returned by an evaluate task in a convergence-envelope handoff.
 * The runtime reads `verdict` to decide whether to proceed or loop back.
 */
export interface ConvergenceEnvelopePayload {
  /** "pass" means quality gate passed; "fail" triggers a loop-back. */
  readonly verdict: "pass" | "fail";
  /** Human-readable feedback injected into the next iteration via `inject_as`. */
  readonly feedback?: string;
}

/** Typed handoff envelope produced by an evaluate task. */
export type ConvergenceEnvelope = HandoffInput<"convergence-envelope", ConvergenceEnvelopePayload>;

export type WorkflowStep = DelegateStep | GateStep | EvaluateStep;

export interface WorkflowContractLike {
  readonly id: string;
  readonly description: string;
  readonly steps: readonly WorkflowStep[];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkflowRunOptions {
  user_request: string;
  /** Override max follow-up attempts per step (lightweight output-format correction). Default: 2 */
  maxFollowUps?: number;
  /** Override max full retry attempts per step. Default: from DSL step.max_retries */
  maxRetries?: number;
  /** Variables propagated to every TaskContext in the workflow (from WorkflowInvocation.context). */
  variables?: Record<string, unknown>;
  onGate?: (gateKind: string, description: string, stepResults: StepResult[]) => Promise<boolean>;
  onStepComplete?: (event: StepCompleteEvent) => void;
  onOptionalStep?: (step: DelegateStep, context: TaskContext) => Promise<boolean>;
  /**
   * Writable stream for real-time progress output during agent execution.
   * Intermediate events from adapters are formatted and written here.
   * Typical value: process.stderr.
   */
  progressOutput?: { write(chunk: string): unknown };
  /**
   * Optional observer emit function for agent-axis spans (agent.session, agent.subagent).
   * When undefined, no spans are emitted (observer is optional — no-op).
   * Pass EventCollector.emit.bind(collector) to wire up the full observability pipeline.
   */
  observer?: ObserverEmitFn;
  /**
   * Optional toolchain-check observation config. When set together with `observer`,
   * runWorkflow runs the configured toolchain checks AFTER the workflow body (a
   * single post-run observation pass) and emits one toolchain.execution span per
   * check, correlated to this run's agent.session span.
   *
   * OBSERVATION ONLY (non-destructive): the checks never alter the WorkflowResult
   * — a failing check does not fail the workflow. `observer` and `parentSpanId`
   * are injected by runWorkflow; callers supply `toolchainsPath` (+ optional
   * artifactsPath/cwd/policy actions).
   */
  toolchain?: RunToolchainChecksOptions;
  /**
   * In-process library tools to register with every task in this workflow.
   * Passed through to each runTask() call. Adapters convert these to their
   * native tool format (Claude MCP, OpenAI function tools, ADK tools).
   */
  libraryTools?: LibraryToolRegistry;
  /**
   * Resolves adapter + model for execution envelopes (agent.session / agent.subagent).
   * When undefined, envelopes omit adapter/model (observer spans lack gen_ai attrs).
   */
  resolveModelMeta?: (modelClass?: ModelClass) => ResolvedModel;
}

// ---------------------------------------------------------------------------
// WorkflowInvocation — SDK-independent structured envelope (G3 / G1)
// ---------------------------------------------------------------------------

/** Typed handoff envelope for workflow input. */
export interface HandoffInput<
  TType extends string = string,
  TPayload = unknown,
> {
  readonly type: TType;
  readonly version?: number;
  readonly payload: TPayload;
}

/**
 * Standard runtime invocation envelope. SDK-specific options (openaiThreadId,
 * sessionId, etc.) belong on the adapter constructor — not here.
 */
export interface WorkflowInvocation<
  THandoffType extends string = string,
  TPayload = unknown,
> {
  workflow: string;

  /** Structured typed handoff input. Takes precedence over user_request if both are set. */
  handoff?: HandoffInput<THandoffType, TPayload>;

  /** Simple string input (convenience shorthand). */
  user_request?: string;

  runtime?: {
    maxFollowUps?: number;
    maxRetries?: number;
    timeoutMs?: number;
    readonly?: boolean;
    dryRun?: boolean;
  };

  hooks?: {
    onStepComplete?: (event: StepCompleteEvent) => void;
    onGate?: (gateKind: string, description: string, stepResults: StepResult[]) => Promise<boolean>;
    onOptionalStep?: (step: DelegateStep, context: TaskContext) => Promise<boolean>;
  };

  /**
   * Writable stream for real-time progress output during agent execution.
   * Intermediate events from adapters are formatted and written here.
   * Typical value: process.stderr.
   */
  progressOutput?: { write(chunk: string): unknown };

  context?: {
    cwd?: string;
    environment?: "local" | "ci" | "remote";
    artifacts?: Record<string, string>;
    variables?: Record<string, unknown>;
  };

  /**
   * Optional observer emit function for agent-axis spans (agent.session, agent.subagent).
   * When undefined, no spans are emitted (observer is optional — no-op).
   * Pass EventCollector.emit.bind(collector) to wire up the full observability pipeline.
   */
  observer?: ObserverEmitFn;
  /**
   * Optional toolchain-check observation config (see WorkflowRunOptions.toolchain).
   * OBSERVATION ONLY — never changes the workflow outcome.
   */
  toolchain?: RunToolchainChecksOptions;
  /**
   * In-process library tools to register with every task in this workflow.
   * Created once per execution and shared across all steps.
   */
  libraryTools?: LibraryToolRegistry;
  /**
   * Resolves adapter + model for execution envelopes. Injected by executeWorkflow;
   * not typically set by external callers.
   */
  resolveModelMeta?: (modelClass?: ModelClass) => ResolvedModel;
}

export interface StepResult {
  step_index: number;
  task_id?: string;
  gate_kind?: string;
  outcome: TaskOutcome | { status: "gate_approved" } | { status: "gate_rejected" } | { status: "skipped" };
  follow_ups_used: number;
  retries_used: number;
  elapsed_ms: number;
  /** Resolved model_class for delegate/evaluate steps (from task registry). */
  model_class?: ModelClass;
  /** Concrete adapter name used for this step's LLM calls. */
  adapter?: string;
  /** Concrete model name used for this step's LLM calls. */
  model?: string;
}

export interface StepCompleteEvent {
  workflow_id: string;
  step_index: number;
  step_type: "delegate" | "gate" | "evaluate";
  task_id?: string;
  gate_kind?: string;
  outcome_status: string;
  follow_ups_used: number;
  retries_used: number;
  elapsed_ms: number;
}

export interface WorkflowResult {
  workflow_id: string;
  status: "completed" | "escalated" | "gate_rejected" | "error";
  steps: StepResult[];
  total_elapsed_ms: number;
  escalation_reason?: string;
  error_message?: string;
}

export interface WorkflowRegistries {
  workflowRegistry?: Record<string, WorkflowContractLike>;
  taskRegistry?: Record<string, TaskContractLike>;
  agentRegistry?: Record<string, AgentContractLike>;
  handoffSchemas?: Record<string, z.ZodType>;
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Resolve a WorkflowInvocation into the legacy (workflowId, WorkflowRunOptions) pair.
 * If a typed handoff is provided, serialise its payload as the user_request
 * context and attach a structured handoff_context prefix so the prompt builder
 * can reference it.
 */
function resolveInvocation(invocation: WorkflowInvocation): {
  workflowId: string;
  options: WorkflowRunOptions;
} {
  let userRequest: string;
  if (invocation.handoff) {
    const h = invocation.handoff;
    userRequest = invocation.user_request
      ? invocation.user_request
      : typeof h.payload === "string"
        ? h.payload
        : JSON.stringify(h.payload, null, 2);
  } else {
    userRequest = invocation.user_request ?? "";
  }

  const ctx = invocation.context;
  const variables: Record<string, unknown> = { ...ctx?.variables };
  if (ctx?.cwd) variables.cwd = ctx.cwd;
  if (ctx?.environment) variables.environment = ctx.environment;
  if (ctx?.artifacts) variables.artifacts = ctx.artifacts;

  return {
    workflowId: invocation.workflow,
    options: {
      user_request: userRequest,
      maxFollowUps: invocation.runtime?.maxFollowUps,
      maxRetries: invocation.runtime?.maxRetries,
      variables: Object.keys(variables).length > 0 ? variables : undefined,
      onGate: invocation.hooks?.onGate,
      onStepComplete: invocation.hooks?.onStepComplete,
      onOptionalStep: invocation.hooks?.onOptionalStep,
      progressOutput: invocation.progressOutput,
      observer: invocation.observer,
      toolchain: invocation.toolchain,
      libraryTools: invocation.libraryTools,
      resolveModelMeta: invocation.resolveModelMeta,
    },
  };
}

/** @deprecated Use the WorkflowInvocation overload for typed handoff input. */
export function runWorkflow(
  adapter: AdapterOrFactory,
  workflowId: string,
  options: WorkflowRunOptions,
  registries?: WorkflowRegistries,
): Promise<WorkflowResult>;

/** Structured invocation with typed handoff envelope. */
export function runWorkflow(
  adapter: AdapterOrFactory,
  invocation: WorkflowInvocation,
  registries?: WorkflowRegistries,
): Promise<WorkflowResult>;

export async function runWorkflow(
  adapterOrFactory: AdapterOrFactory,
  workflowIdOrInvocation: string | WorkflowInvocation,
  optionsOrRegistries?: WorkflowRunOptions | WorkflowRegistries,
  maybeRegistries?: WorkflowRegistries,
): Promise<WorkflowResult> {
  const { adapter: primaryAdapter, factory: adapterFactory } = resolveAdapter(adapterOrFactory);
  let workflowId: string;
  let options: WorkflowRunOptions;
  let registries: WorkflowRegistries | undefined;
  let invocationHandoff: HandoffInput | undefined;

  if (typeof workflowIdOrInvocation === "string") {
    workflowId = workflowIdOrInvocation;
    options = optionsOrRegistries as WorkflowRunOptions;
    registries = maybeRegistries;
  } else {
    const resolved = resolveInvocation(workflowIdOrInvocation);
    workflowId = resolved.workflowId;
    options = resolved.options;
    registries = optionsOrRegistries as WorkflowRegistries | undefined;
    invocationHandoff = workflowIdOrInvocation.handoff;
  }

  // --- G9: Validate input handoff against schema if available ---
  if (invocationHandoff && registries?.handoffSchemas) {
    const inputSchema = registries.handoffSchemas[invocationHandoff.type];
    if (inputSchema) {
      const validation = inputSchema.safeParse(invocationHandoff.payload);
      if (!validation.success) {
        return {
          workflow_id: workflowId,
          status: "error",
          steps: [],
          total_elapsed_ms: 0,
          error_message: `Input handoff validation failed for "${invocationHandoff.type}": ${validation.error.message}`,
        };
      }
    }
  }

  const wfReg = registries?.workflowRegistry ?? {};
  const workflow = wfReg[workflowId];
  if (!workflow) {
    return {
      workflow_id: workflowId,
      status: "error",
      steps: [],
      total_elapsed_ms: 0,
      error_message: `Unknown workflow: ${workflowId}`,
    };
  }

  await pluginRegistry.runBeforeWorkflow(workflowId, options.user_request);

  // ── ExecutionEnvelope + agent.session open (Phase 1-1, 1-3) ────────────────
  // Create a standard envelope for this workflow session. run_id is a UUIDv7.
  // The sessionSpanId is used as parentSpanId for child agent.subagent spans.
  // Resolve session-open meta as the "standard" model class (NOT undefined).
  // resolve(undefined) short-circuits to the bare fallback and never consults
  // env / model_mapping, so the open span dropped gen_ai.request.model (and
  // gen_ai.system) whenever fallbackModel was unset (e.g. --adapter mock / no
  // --model). Using "standard" mirrors how subagent spans resolve their meta
  // (workflow-runner passes the step's model_class), keeping the session open
  // span's model/adapter consistent with the standard model_mapping entry. (#166)
  const sessionMeta = options.resolveModelMeta?.("standard");
  const sessionEnvelope = createExecutionEnvelope({
    componentId: workflowId,
    adapter: sessionMeta?.adapter,
    model: sessionMeta?.model,
  });
  const sessionSpanId = emitSessionOpen(options.observer, sessionEnvelope, workflowId);

  const workflowStart = Date.now();

  // ── Exception-safe session lifecycle ───────────────────────────────────────
  // The workflow body can throw (e.g. an invalid depends_on graph) AFTER the
  // agent.session open span has been emitted. Wrap the body in try/finally so
  // the close span is ALWAYS emitted — on success, on a returned failure
  // (escalated/error) AND on a thrown exception — leaving no span stuck 'open'.
  // (The body retains its original indentation; only the try/finally frame is new.)
  let wfResult: WorkflowResult | undefined;
  let sessionError: Error | undefined;

  // Token usage accumulation across all steps (declared outside try so finally can read)
  const tokenAccumulator: TokenUsage = { inputTokens: 0, outputTokens: 0, totalTokens: 0 };
  let hasTokenData = false;

  try {

  // Build step identity map: step index → task ID (delegate) or gate_kind (gate)
  const stepId = (i: number): string => {
    const s = workflow.steps[i];
    if (s.type === "delegate" || s.type === "evaluate") return s.task;
    return `gate:${s.gate_kind}`;
  };

  // Resolve depends_on: if specified (even as empty array) use it, otherwise depend on all preceding steps.
  // WORKFLOW_INPUT_STEP_ID is a virtual entry node — always satisfied, not a workflow step index.
  const workflowInputOutput: WorkflowInputOutput = buildWorkflowInputOutput(
    options.user_request,
    invocationHandoff,
  );

  const depsByIndex: number[][] = workflow.steps.map((step, i) => {
    if (step.depends_on !== undefined) {
      return step.depends_on
        .filter((dep) => dep !== WORKFLOW_INPUT_STEP_ID)
        .map((dep) => {
          const idx = workflow.steps.findIndex((s, j) => j < i && stepId(j) === dep);
          if (idx === -1) {
            throw new Error(`depends_on "${dep}" in step ${i} (${stepId(i)}) not found among preceding steps`);
          }
          return idx;
        });
    }
    // Default: depend on all preceding steps (preserves sequential behaviour)
    return i === 0 ? [] : Array.from({ length: i }, (_, k) => k);
  });

  // DAG scheduler state
  const completed = new Set<number>();
  const stepResults: StepResult[] = new Array(workflow.steps.length);
  const stepContexts: string[] = new Array(workflow.steps.length).fill("");
  const stepOutputs: Array<unknown> = new Array(workflow.steps.length).fill(undefined);
  let abortResult: WorkflowResult | undefined;

  // Evaluate-step loop state
  /** Tracks how many times each evaluate step (by index) has executed so far. */
  const evaluateExecCount = new Map<number, number>();
  /** Tracks the current iteration span ID per evaluate step index. */
  const iterationSpanIds = new Map<number, string>();
  /**
   * Variables injected by evaluate steps via `inject_as`.
   * Merged into every subsequent step's TaskContext.variables on loop-back.
   */
  const loopInjectedVars: Record<string, unknown> = {};

  /** Extract a dotted path from a structured object. */
  function extractPath(obj: unknown, path: string): unknown {
    const cleanPath = path.startsWith("$.") ? path.slice(2) : path;
    const segments = cleanPath.split(".");
    let current: unknown = obj;
    for (const seg of segments) {
      if (current == null || typeof current !== "object") return undefined;
      current = (current as Record<string, unknown>)[seg];
    }
    return current;
  }

  /** Build typed handoff input from receives declarations. */
  function projectHandoffs(
    stepIndex: number,
    step: DelegateStep,
  ): Record<string, unknown> | undefined {
    if (!step.receives) return undefined;

    const projected: Record<string, unknown> = {};

    for (const [alias, spec] of Object.entries(step.receives)) {
      const fromTaskId = spec.from ?? alias;

      if (fromTaskId === WORKFLOW_INPUT_STEP_ID) {
        if (spec.path) {
          projected[alias] = extractPath(workflowInputOutput, spec.path);
        } else {
          projected[alias] = workflowInputOutput;
        }
        continue;
      }

      const depIdx = workflow.steps.findIndex((s, j) => j < stepIndex && stepId(j) === fromTaskId);
      if (depIdx === -1) continue;

      const output = stepOutputs[depIdx];
      if (output === undefined) continue;

      if (spec.path) {
        projected[alias] = extractPath(output, spec.path);
      } else {
        projected[alias] = output;
      }
    }

    return Object.keys(projected).length > 0 ? projected : undefined;
  }

  /** Build accumulated context for a step from its dependency chain. */
  function contextFor(stepIndex: number): string {
    const step = workflow.steps[stepIndex];
    const parts: string[] = [];

    if (stepDependsOnWorkflowInput(step.depends_on)) {
      parts.push(formatWorkflowInputContext(workflowInputOutput));
    }

    const deps = depsByIndex[stepIndex];
    if (deps.length > 0) {
      parts.push(
        ...deps
          .filter((d) => completed.has(d))
          .map((d) => stepContexts[d])
          .filter(Boolean),
      );
    }

    return parts.join("");
  }

  type StepResultWithTokens = StepResult & { tokenUsage?: TokenUsage };

  function stepModelFields(
    modelClass: ModelClass | undefined,
    envelopeMeta?: ResolvedModel,
  ): Pick<StepResult, "model_class" | "adapter" | "model"> {
    if (!modelClass && !envelopeMeta) return {};
    return {
      ...(modelClass ? { model_class: modelClass } : {}),
      ...(envelopeMeta?.adapter ? { adapter: envelopeMeta.adapter } : {}),
      ...(envelopeMeta?.model ? { model: envelopeMeta.model } : {}),
    };
  }

  /** Execute a single step (delegate or gate). */
  async function executeStep(
    i: number,
    stepAdapter: SdkAdapter,
    envelopeMeta?: ResolvedModel,
    modelClass?: ModelClass,
  ): Promise<StepResultWithTokens> {
    const step = workflow.steps[i];
    const stepStart = Date.now();

    if (step.type === "gate") {
      return handleGateStep(step, Object.values(stepResults).filter(Boolean), options, i, stepAdapter);
    }

    // ── Evaluate step ────────────────────────────────────────────────────────
    if (step.type === "evaluate") {
      const evaluateStep = step as EvaluateStep;
      const iterationNumber = (evaluateExecCount.get(i) ?? 0) + 1;
      const iterationSpanId = emitIterationOpen(
        options.observer,
        sessionSpanId,
        evaluateStep.task,
        evaluateStep.loop_to,
        iterationNumber,
        evaluateStep.max_iterations,
      );
      if (iterationSpanId) {
        iterationSpanIds.set(i, iterationSpanId);
      }
      // Merge workflow variables with any loop-injected feedback variables
      const effectiveVariables = mergeVariables(options.variables, loopInjectedVars);
      const context: TaskContext = {
        user_request: evaluateStep.description ?? options.user_request,
        prior_context: contextFor(i),
        ...(effectiveVariables ? { variables: effectiveVariables } : {}),
      };
      const taskResult: TaskRunResult = await runTask(stepAdapter, evaluateStep.task, context, {
        maxFollowUps: options.maxFollowUps ?? 2,
        maxRetries: 0, // evaluate steps do not retry
        taskRegistry: registries?.taskRegistry,
        agentRegistry: registries?.agentRegistry,
        handoffSchemas: registries?.handoffSchemas,
        progressOutput: options.progressOutput,
        observer: options.observer,
        parentSpanId: iterationSpanId ?? sessionSpanId,
        sessionId: sessionSpanId ?? sessionEnvelope.run_id,
        libraryTools: options.libraryTools,
        envelopeAdapter: envelopeMeta?.adapter,
        envelopeModel: envelopeMeta?.model,
      });
      return {
        step_index: i,
        task_id: evaluateStep.task,
        outcome: taskResult.outcome,
        follow_ups_used: taskResult.follow_ups_used,
        retries_used: taskResult.retries_used,
        elapsed_ms: Date.now() - stepStart,
        tokenUsage: taskResult.tokenUsage,
        ...stepModelFields(modelClass, envelopeMeta),
      };
    }

    const delegateStep = step as DelegateStep;

    // Optional steps
    if (step.optional) {
      const stepUserRequest = delegateStep.instruction ?? options.user_request;
      // Merge workflow variables with any loop-injected feedback variables
      const ctxVars = mergeVariables(options.variables, loopInjectedVars);
      const ctx: TaskContext = {
        user_request: stepUserRequest,
        prior_context: contextFor(i),
        ...(ctxVars ? { variables: ctxVars } : {}),
      };
      const shouldRun = options.onOptionalStep
        ? await options.onOptionalStep(step, ctx)
        : false;

      if (!shouldRun) {
        return {
          step_index: i,
          task_id: step.task,
          outcome: { status: "skipped" },
          follow_ups_used: 0,
          retries_used: 0,
          elapsed_ms: Date.now() - stepStart,
          ...stepModelFields(modelClass, envelopeMeta),
        };
      }
    }

    // Execute task with followUp/retry recovery
    const stepUserRequest = delegateStep.instruction ?? options.user_request;

    let handoffInput: TaskContext["handoff_input"] | undefined;
    const projected = projectHandoffs(i, delegateStep);
    if (projected) {
      handoffInput = {
        type: "step-projection",
        payload: projected,
      };
    } else if (i === 0 && invocationHandoff) {
      handoffInput = invocationHandoff;
    }

    const useLegacyContext = !delegateStep.receives;
    // Merge workflow variables with any loop-injected feedback variables
    const delegateVars = mergeVariables(options.variables, loopInjectedVars);

    const context: TaskContext = {
      user_request: stepUserRequest,
      ...(useLegacyContext ? { prior_context: contextFor(i) } : {}),
      ...(delegateVars ? { variables: delegateVars } : {}),
      ...(handoffInput ? { handoff_input: handoffInput } : {}),
    };
    const taskResult: TaskRunResult = await runTask(stepAdapter, step.task, context, {
      maxFollowUps: options.maxFollowUps ?? step.max_follow_ups ?? 2,
      maxRetries: options.maxRetries ?? step.max_retries ?? 0,
      taskRegistry: registries?.taskRegistry,
      agentRegistry: registries?.agentRegistry,
      handoffSchemas: registries?.handoffSchemas,
      retry: step.retry,
      progressOutput: options.progressOutput,
      observer: options.observer,
      parentSpanId: sessionSpanId,
      sessionId: sessionSpanId ?? sessionEnvelope.run_id,
      libraryTools: options.libraryTools,
      envelopeAdapter: envelopeMeta?.adapter,
      envelopeModel: envelopeMeta?.model,
    });
    return {
      step_index: i,
      task_id: step.task,
      outcome: taskResult.outcome,
      follow_ups_used: taskResult.follow_ups_used,
      retries_used: taskResult.retries_used,
      elapsed_ms: Date.now() - stepStart,
      tokenUsage: taskResult.tokenUsage,
      ...stepModelFields(modelClass, envelopeMeta),
    };
  }

  // DAG execution loop: on each iteration, find all steps whose deps are satisfied
  // and run them concurrently.
  while (completed.size < workflow.steps.length) {
    const ready: number[] = [];
    for (let i = 0; i < workflow.steps.length; i++) {
      if (completed.has(i)) continue;
      if (depsByIndex[i].every((d) => completed.has(d))) {
        ready.push(i);
      }
    }

    if (ready.length === 0) {
      // Should not happen with valid DAG; safeguard against cycles
      abortResult = {
        workflow_id: workflow.id,
        status: "error",
        steps: stepResults.filter(Boolean),
        total_elapsed_ms: Date.now() - workflowStart,
        error_message: "Dependency cycle detected: no steps are ready to execute",
      };
      break;
    }

    const batchResults = await Promise.all(ready.map(async (i) => {
      const step = workflow.steps[i];
      const modelClass = (step.type === "delegate" || step.type === "evaluate")
        ? (registries?.taskRegistry?.[step.task]?.model_class as ModelClass | undefined)
        : undefined;

      let stepAdapter: SdkAdapter;
      if (ready.length > 1 && adapterFactory) {
        const result = adapterFactory(modelClass);
        stepAdapter = (result && typeof (result as Promise<SdkAdapter>).then === "function")
          ? await (result as Promise<SdkAdapter>)
          : result as SdkAdapter;
      } else if (modelClass && adapterFactory) {
        const result = adapterFactory(modelClass);
        stepAdapter = (result && typeof (result as Promise<SdkAdapter>).then === "function")
          ? await (result as Promise<SdkAdapter>)
          : result as SdkAdapter;
      } else {
        stepAdapter = primaryAdapter;
      }
      const envelopeMeta = options.resolveModelMeta?.(modelClass);
      return executeStep(i, stepAdapter, envelopeMeta, modelClass);
    }));

    for (let b = 0; b < ready.length; b++) {
      const i = ready[b];
      const result = batchResults[b];
      const step = workflow.steps[i];

      // Accumulate token usage from this step
      if (result.tokenUsage) {
        tokenAccumulator.inputTokens += result.tokenUsage.inputTokens;
        tokenAccumulator.outputTokens += result.tokenUsage.outputTokens;
        tokenAccumulator.totalTokens = (tokenAccumulator.totalTokens ?? 0) + (result.tokenUsage.totalTokens ?? 0);
        if (result.tokenUsage.cacheReadTokens) {
          tokenAccumulator.cacheReadTokens = (tokenAccumulator.cacheReadTokens ?? 0) + result.tokenUsage.cacheReadTokens;
        }
        if (result.tokenUsage.cacheCreationTokens) {
          tokenAccumulator.cacheCreationTokens = (tokenAccumulator.cacheCreationTokens ?? 0) + result.tokenUsage.cacheCreationTokens;
        }
        hasTokenData = true;
      }

      // ── Evaluate step post-processing ─────────────────────────────────────
      if (step.type === "evaluate") {
        const evaluateStep = step as EvaluateStep;

        // Extract verdict from the evaluate task's structured output
        const data = result.outcome.status === "success"
          ? (result.outcome as { status: "success"; data: unknown }).data as Record<string, unknown>
          : undefined;
        const verdict = (typeof data?.verdict === "string" ? data.verdict : "fail") as string;

        const execCount = (evaluateExecCount.get(i) ?? 0) + 1;

        // Close iteration span for this execution
        const currentIterationSpanId = iterationSpanIds.get(i);
        if (currentIterationSpanId) {
          const willLoopBack = verdict !== "pass" && execCount < evaluateStep.max_iterations;
          const feedbackInjected = willLoopBack && !!evaluateStep.inject_as;
          emitIterationClose(
            options.observer,
            currentIterationSpanId,
            evaluateStep.task,
            execCount,
            verdict,
            result.elapsed_ms,
            feedbackInjected,
          );
          iterationSpanIds.delete(i);
        }

        if (verdict === "pass") {
          // Quality gate passed — record result and proceed
          emitIterationMetric(
            options.observer,
            sessionSpanId,
            evaluateStep.task,
            execCount,
            "pass",
          );
          stepResults[i] = result;
          completed.add(i);
          emitStepEvent(options, workflow.id, i, step, result);
          if (data !== undefined) {
            stepOutputs[i] = data;
            stepContexts[i] = `\n\n--- Step ${i + 1}: ${stepId(i)} ---\n${JSON.stringify(data, null, 2)}`;
          }
        } else if (execCount < evaluateStep.max_iterations) {
          // Quality gate failed but iterations remain — inject feedback and loop back
          evaluateExecCount.set(i, execCount);

          if (evaluateStep.inject_as) {
            const feedback = typeof data?.feedback === "string"
              ? data.feedback
              : (data !== undefined ? JSON.stringify(data) : "Evaluate step returned no feedback");
            loopInjectedVars[evaluateStep.inject_as] = feedback;
          }

          // Find the loop_to step (must precede this evaluate step)
          const loopToIdx = workflow.steps.findIndex((s, j) => j < i && stepId(j) === evaluateStep.loop_to);
          if (loopToIdx === -1) {
            stepResults[i] = result;
            completed.add(i);
            abortResult = {
              workflow_id: workflow.id,
              status: "error",
              steps: stepResults.filter(Boolean),
              total_elapsed_ms: Date.now() - workflowStart,
              error_message: `Evaluate step "${evaluateStep.task}" references unknown loop_to "${evaluateStep.loop_to}"`,
            };
          } else {
            // Reset all steps in the loop range [loopToIdx, i] so they re-execute
            for (let k = loopToIdx; k <= i; k++) {
              completed.delete(k);
              stepOutputs[k] = undefined;
              stepContexts[k] = "";
            }
            // Evaluate step result is intermediate — do not persist it
          }
        } else {
          // Max iterations exhausted — apply on_exhausted policy
          evaluateExecCount.set(i, execCount);
          stepResults[i] = result;
          completed.add(i);

          const exhaustedPolicy = evaluateStep.on_exhausted ?? "escalate";
          emitIterationMetric(
            options.observer,
            sessionSpanId,
            evaluateStep.task,
            execCount,
            `exhausted-${exhaustedPolicy}`,
          );

          switch (exhaustedPolicy) {
            case "abort":
              abortResult = {
                workflow_id: workflow.id,
                status: "error",
                steps: stepResults.filter(Boolean),
                total_elapsed_ms: Date.now() - workflowStart,
                error_message: `Evaluate step "${evaluateStep.task}" exhausted ${evaluateStep.max_iterations} iterations without passing`,
              };
              break;
            case "fail_partial":
              // Degraded success — continue workflow without evaluate outputs
              emitStepEvent(options, workflow.id, i, step, result);
              break;
            case "escalate":
            default:
              abortResult = {
                workflow_id: workflow.id,
                status: "escalated",
                steps: stepResults.filter(Boolean),
                total_elapsed_ms: Date.now() - workflowStart,
                escalation_reason: `Evaluate step "${evaluateStep.task}" exhausted ${evaluateStep.max_iterations} iterations without passing`,
              };
              break;
          }
        }
        continue; // Skip generic post-processing below
      }

      // ── Generic delegate / gate post-processing ───────────────────────────
      stepResults[i] = result;
      completed.add(i);
      emitStepEvent(options, workflow.id, i, step, result);

      // Store context fragment and structured output for dependants
      if (result.outcome.status === "success") {
        const data = (result.outcome as { data: unknown }).data;
        stepOutputs[i] = data;
        stepContexts[i] = `\n\n--- Step ${i + 1}: ${stepId(i)} ---\n` +
          (typeof data === "string" ? data : JSON.stringify(data, null, 2));
      }

      // Abort-worthy outcomes
      if (result.outcome.status === "gate_rejected") {
        abortResult = {
          workflow_id: workflow.id,
          status: "gate_rejected",
          steps: stepResults.filter(Boolean),
          total_elapsed_ms: Date.now() - workflowStart,
        };
      } else if (result.outcome.status === "escalation") {
        abortResult = {
          workflow_id: workflow.id,
          status: "escalated",
          steps: stepResults.filter(Boolean),
          total_elapsed_ms: Date.now() - workflowStart,
          escalation_reason: (result.outcome as { reason: string }).reason,
        };
      } else if (result.outcome.status === "error" || result.outcome.status === "validation_error") {
        const taskId = step.type === "delegate" ? step.task : (step as GateStep).gate_kind;
        abortResult = {
          workflow_id: workflow.id,
          status: "escalated",
          steps: stepResults.filter(Boolean),
          total_elapsed_ms: Date.now() - workflowStart,
          escalation_reason: `Task "${taskId}" failed after ${result.follow_ups_used} follow-ups and ${result.retries_used} retries`,
        };
      }
    }

    if (abortResult) break;
  }

  wfResult = abortResult ?? {
    workflow_id: workflow.id,
    status: "completed",
    steps: stepResults.filter(Boolean),
    total_elapsed_ms: Date.now() - workflowStart,
  };
  await pluginRegistry.runAfterWorkflow(workflow.id, wfResult);

  // ── toolchain.execution observation pass (post-run, non-destructive) ───────
  // Integration point: a single observation pass AFTER the workflow body, rather
  // than per-step. Rationale — toolchain checks (lint/format/drift) inspect the
  // artifacts a run produced, so the natural correlation is the run as a whole;
  // running once post-body keeps the spans children of the still-open session and
  // avoids coupling toolchain config to individual step semantics. The observer
  // and parent span are injected here so checks emit toolchain.execution spans
  // correlated to this session. Wrapped in its own try/catch so a toolchain or
  // subprocess failure can NEVER change the workflow outcome (observation only).
  if (options.toolchain && options.observer) {
    try {
      await runToolchainChecks({
        ...options.toolchain,
        observer: options.observer,
        parentSpanId: sessionSpanId,
        // Propagate session correlation to any git spawned by a check (#115):
        // the session id is the agent.session run_id (== sessionSpanId).
        observabilityIds: {
          sessionId: sessionSpanId ?? sessionEnvelope.run_id,
          runId: sessionEnvelope.run_id,
        },
      });
    } catch {
      // Observation must never affect the workflow result — swallow.
    }
  }

  return wfResult;
  } catch (err) {
    sessionError = err as Error;
    throw err;
  } finally {
    // ── agent.session close (Phase 1-3) ──────────────────────────────────────
    // Emit close span for the workflow session on EVERY exit path. No-op when
    // observer is undefined. On a thrown exception wfResult is undefined → the
    // status is recorded as 'error' with the cause in `agent.error`.
    if (sessionSpanId) {
      const durationMs = wfResult?.total_elapsed_ms ?? (Date.now() - workflowStart);
      sessionEnvelope.execution_metadata = {
        duration_ms: durationMs,
        ...(hasTokenData ? {
          token_usage: {
            input_tokens: tokenAccumulator.inputTokens,
            output_tokens: tokenAccumulator.outputTokens,
            total_tokens: tokenAccumulator.totalTokens ?? (tokenAccumulator.inputTokens + tokenAccumulator.outputTokens),
            ...(tokenAccumulator.cacheReadTokens ? { cache_read_input_tokens: tokenAccumulator.cacheReadTokens } : {}),
            ...(tokenAccumulator.cacheCreationTokens ? { cache_creation_input_tokens: tokenAccumulator.cacheCreationTokens } : {}),
          },
        } : {}),
      };
      if (hasTokenData) {
        const costUsd = computeCostUsd(
          sessionEnvelope.model,
          tokenAccumulator.inputTokens,
          tokenAccumulator.outputTokens,
        );
        if (costUsd !== undefined) {
          sessionEnvelope.execution_metadata = {
            ...sessionEnvelope.execution_metadata,
            cost_usd: costUsd,
          };
        }
      }
      emitSessionClose(
        options.observer,
        sessionSpanId,
        sessionEnvelope,
        durationMs,
        wfResult?.status ?? "error",
        sessionError?.message,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Merge base variables with injected loop variables.
 * Returns undefined when the merged result is empty (avoids adding an empty
 * `variables: {}` to TaskContext which would be misleading).
 */
function mergeVariables(
  base: Record<string, unknown> | undefined,
  injected: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const merged = { ...base, ...injected };
  return Object.keys(merged).length > 0 ? merged : undefined;
}

async function handleGateStep(
  step: GateStep,
  priorResults: StepResult[],
  options: WorkflowRunOptions,
  stepIndex: number,
  adapter?: SdkAdapter,
): Promise<StepResult> {
  const start = Date.now();

  if (options.onGate) {
    const approved = await options.onGate(step.gate_kind, step.description, priorResults);
    return {
      step_index: stepIndex,
      gate_kind: step.gate_kind,
      outcome: { status: approved ? "gate_approved" : "gate_rejected" },
      follow_ups_used: 0,
      retries_used: 0,
      elapsed_ms: Date.now() - start,
    };
  }

  // LLM-based gate evaluation using the description as the condition
  if (adapter && step.description) {
    const approved = await evaluateGateWithLlm(adapter, step, priorResults);
    return {
      step_index: stepIndex,
      gate_kind: step.gate_kind,
      outcome: { status: approved ? "gate_approved" : "gate_rejected" },
      follow_ups_used: 0,
      retries_used: 0,
      elapsed_ms: Date.now() - start,
    };
  }

  // Fallback: no onGate, no adapter or no description — auto-approve with warning
  return {
    step_index: stepIndex,
    gate_kind: step.gate_kind,
    outcome: { status: "gate_approved" },
    follow_ups_used: 0,
    retries_used: 0,
    elapsed_ms: Date.now() - start,
  };
}

/**
 * Use the LLM adapter to evaluate a gate condition.
 * Sends a lightweight prompt with the gate description and prior step outcomes,
 * expecting a JSON response with { "approved": boolean, "reason": string }.
 */
async function evaluateGateWithLlm(
  adapter: SdkAdapter,
  step: GateStep,
  priorResults: StepResult[],
): Promise<boolean> {
  const priorContext = priorResults
    .map((r) => {
      const id = r.task_id ?? r.gate_kind ?? `step_${r.step_index}`;
      const data = "data" in r.outcome ? (r.outcome as { data: unknown }).data : undefined;
      return `- ${id}: status=${r.outcome.status}${data ? `, data=${typeof data === "string" ? data : JSON.stringify(data)}` : ""}`;
    })
    .join("\n");

  const prompt = buildGateEvaluationPrompt(step, priorContext);

  try {
    const response = await adapter.send(prompt, { readonly: true });
    return parseGateEvaluationResponse(response);
  } catch {
    // On LLM failure, reject the gate (fail-closed)
    return false;
  }
}

export function buildGateEvaluationPrompt(step: GateStep, priorContext: string): string {
  return `You are a gate evaluator in a workflow pipeline. Your job is to decide whether the workflow should proceed based on prior step results.

## Gate
- kind: ${step.gate_kind}
- condition: ${step.description}

## Prior Step Results
${priorContext || "(no prior results)"}

## Instructions
Evaluate whether the gate condition is satisfied based on the prior step results.
Respond with ONLY a JSON object (no markdown fences, no extra text):

{"approved": true, "reason": "brief explanation"}

or

{"approved": false, "reason": "brief explanation"}`;
}

export function parseGateEvaluationResponse(response: string): boolean {
  // Try to extract JSON from the response
  const jsonMatch = response.match(/\{[\s\S]*?"approved"\s*:\s*(true|false)[\s\S]*?\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      return parsed.approved === true;
    } catch {
      // fall through
    }
  }

  // Heuristic fallback: look for explicit approval/rejection keywords
  const lower = response.toLowerCase();
  if (lower.includes('"approved": true') || lower.includes('"approved":true')) {
    return true;
  }
  if (lower.includes('"approved": false') || lower.includes('"approved":false')) {
    return false;
  }

  // Fail-closed: if we can't parse, reject
  return false;
}

function emitStepEvent(
  options: WorkflowRunOptions,
  workflowId: string,
  stepIndex: number,
  step: WorkflowStep,
  result: StepResult,
): void {
  options.onStepComplete?.({
    workflow_id: workflowId,
    step_index: stepIndex,
    step_type: step.type,
    task_id: step.type === "delegate" ? (step as DelegateStep).task : undefined,
    gate_kind: step.type === "gate" ? (step as GateStep).gate_kind : undefined,
    outcome_status: result.outcome.status,
    follow_ups_used: result.follow_ups_used,
    retries_used: result.retries_used,
    elapsed_ms: result.elapsed_ms,
  });
}
